// "Units left by aisle" — a popup the picker opens from the route button
// in the top bar (next to Pause and the flag). A plain list: every
// location still to pick with its quantity, grouped under the aisle
// pair it belongs to (K–L, M–N, AA…) with the pair's total, in the
// wave's pick order. The location the picker is standing at is marked
// in brand orange with "here". Kept deliberately simple — picker
// feedback via Alex (2026-10-13) was that an earlier tunnel-side split
// with two colours was over-complicated.
//
// Tap a location to pick from there next (Alex, 2026-10-13). The row
// hands its barcode to the parent, which sends the same skip-to-location
// round-trip the hero's location pin uses: the walk re-anchors on that
// location and everything before it in the pick order defers to the
// end, so nothing is lost — the picker sweeps back through it last.
// The parent only passes `onSelect` while a skip is allowed (same gate
// as the pin: on the scan hero, online, nothing left to sync); without
// it the rows are plain text. The current location is never a button —
// jumping to where you already are is a no-op.
//
// Same shell as AlternateBarcodesModal: display-only otherwise, scanning
// stays live underneath — the global keydown listener is on the parent
// — so a picker can read it and scan straight on; the X or the backdrop
// closes it. The modal card scrolls, so a 50-line Large wave is fine.
import { ChevronRight, X } from "lucide-react";
import { useTranslation } from "../../i18n";
import type { AisleGroup, AisleLocation } from "./aisleUnits";
import "./PickingScreen.css";

type Props = {
    groups: AisleGroup[];
    onClose: () => void;
    /** Tap-to-jump: called with the row's location BARCODE. Omitted →
     *  rows are not tappable. */
    onSelect?: (location: string) => void;
    /** Holds the rows while the parent is busy (a skip in flight, a
     *  scan submitting). */
    selectDisabled?: boolean;
};

const LocationRow = ({ loc, onSelect, disabled }: {
    loc: AisleLocation;
    onSelect?: (location: string) => void;
    disabled: boolean;
}) => {
    const { t } = useTranslation();
    const label = t("picking_aisle_location_units", { location: loc.display, n: String(loc.remaining) });
    const body = (
        <>
            <span className="ps-aisle-loc-code">{loc.display}</span>
            {loc.current && (
                <span className="ps-aisle-loc-here">{t("picking_aisle_units_here")}</span>
            )}
            <span className="ps-aisle-loc-n">{loc.remaining}</span>
        </>
    );
    const className = "ps-aisle-loc" + (loc.current ? " ps-aisle-loc--current" : "");
    if (onSelect && !loc.current) {
        return (
            <li className={className + " ps-aisle-loc--tap"} aria-label={label}>
                <button
                    type="button"
                    className="ps-aisle-loc-btn"
                    onClick={() => onSelect(loc.location)}
                    disabled={disabled}
                    aria-label={t("picking_aisle_jump_label", { location: loc.display })}
                >
                    {body}
                    <ChevronRight className="ps-aisle-loc-chevron" size={18} strokeWidth={2.25} aria-hidden="true" />
                </button>
            </li>
        );
    }
    return (
        <li className={className} aria-current={loc.current ? "location" : undefined} aria-label={label}>
            {body}
        </li>
    );
};

const AisleSummary = ({ groups, onClose, onSelect, selectDisabled }: Props) => {
    const { t } = useTranslation();
    const total = groups.reduce((sum, g) => sum + g.remaining, 0);
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-card ps-aisles-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2>{t("picking_aisle_units_title")}</h2>
                    <button className="modal-close" onClick={onClose} aria-label={t("close")}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    <div className="ps-aisles-total-row">
                        <span className="ps-more-eyebrow">{t("picking_aisle_units_hint")}</span>
                        <span className="ps-aisles-total">
                            {t("picking_aisle_units_total", { n: String(total) })}
                        </span>
                    </div>
                    {onSelect && (
                        <div className="ps-aisles-tap-hint">{t("picking_aisle_tap_hint")}</div>
                    )}
                    <ul className="ps-aisles">
                        {groups.map(g => (
                            <li
                                key={g.key}
                                className={"ps-aisle-group" + (g.current ? " ps-aisle-group--current" : "")}
                                aria-label={t("picking_aisle_units_chip", { aisle: g.label, n: String(g.remaining) })}
                            >
                                <div className="ps-aisle-group-head">
                                    <span className="ps-aisle-group-label">{g.label}</span>
                                    <span className="ps-aisle-group-n">{g.remaining}</span>
                                </div>
                                <ul className="ps-aisle-locs">
                                    {g.locations.map(l => (
                                        <LocationRow
                                            key={l.location}
                                            loc={l}
                                            onSelect={onSelect}
                                            disabled={!!selectDisabled}
                                        />
                                    ))}
                                </ul>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default AisleSummary;
